/**
 * 3D model — a scene she can turn in her hands.
 *
 * Three scenes exist because three questions keep coming up: why it gets dark,
 * what else is out there, and what is inside a body. Labels may only name parts
 * the chosen scene actually contains, so a label can never point at nothing.
 */

import { z } from 'zod'
import { IdSchema, LabelSchema, SentenceSchema, isUnique, specFor } from '../common'
import { BODY_SYSTEMS, BODY_SYSTEM_PARTS, MODEL_PARTS, MODEL_TYPES, PLANETS } from '../content'

export const MODEL_INTERACTIONS = [
  'rotate',
  'zoom',
  'play_pause',
  'scrub_time',
  'toggle_labels',
  'tap_parts',
] as const

export const CAMERA_PRESETS = ['front', 'top', 'orbit', 'close'] as const

export const ModelLabelSchema = z.object({
  id: IdSchema,
  partKey: LabelSchema.max(40),
  text: LabelSchema,
  visibleByDefault: z.boolean().default(true),
})

export const Model3dConfigSchema = z
  .object({
    modelType: z.enum(MODEL_TYPES),
    interactions: z.array(z.enum(MODEL_INTERACTIONS)).min(1).max(6),
    labels: z.array(ModelLabelSchema).max(12).default([]),
    cameraPreset: z.enum(CAMERA_PRESETS).default('orbit'),
    autoRotate: z.boolean().default(false),
    /** Day lengths per second when time is running. */
    timeScale: z.number().min(0.05).max(20).default(1),
    /** Which planets are in the scene. Solar system only. */
    planetsShown: z.array(z.enum(PLANETS)).min(1).max(8).optional(),
    /** Which system is on show. Body model only. */
    bodySystem: z.enum(BODY_SYSTEMS).optional(),
    /** The question to ask while she turns it: "Where is it night now?" */
    narrationPrompt: SentenceSchema.optional(),
  })
  .superRefine((config, ctx) => {
    if (!isUnique(config.interactions)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['interactions'], message: 'list each interaction once' })
    }
    if (!isUnique(config.labels.map((label) => label.id))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['labels'], message: 'label ids must be unique' })
    }

    if (config.modelType === 'solar_system' && !config.planetsShown) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['planetsShown'],
        message: 'say which planets are in the scene',
      })
    }
    if (config.modelType === 'body_systems' && !config.bodySystem) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bodySystem'], message: 'say which body system is on show' })
    }
    if (config.modelType !== 'solar_system' && config.planetsShown) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['planetsShown'],
        message: 'only the solar system scene has planets',
      })
    }
    if (config.modelType !== 'body_systems' && config.bodySystem) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bodySystem'],
        message: 'only the body scene has a body system',
      })
    }
    if (config.modelType !== 'earth_sun_day_night' && config.interactions.includes('scrub_time')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['interactions'],
        message: 'only the day and night scene has time to scrub',
      })
    }

    const allowed = new Set<string>(
      config.modelType === 'body_systems' && config.bodySystem
        ? BODY_SYSTEM_PARTS[config.bodySystem]
        : config.modelType === 'solar_system' && config.planetsShown
          ? ['sun', 'orbit_path', 'asteroid_belt', ...config.planetsShown]
          : MODEL_PARTS[config.modelType],
    )
    for (const [index, label] of config.labels.entries()) {
      if (!allowed.has(label.partKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['labels', index, 'partKey'],
          message: `"${label.partKey}" is not in this scene`,
        })
      }
    }
  })

export type Model3dConfig = z.infer<typeof Model3dConfigSchema>

export const Model3dSpecSchema = specFor('model_3d', Model3dConfigSchema)
